import { useEffect, useState } from 'react';
import { useParams, Link } from 'react-router-dom';
import { Users, Calendar, Settings, Clock, ArrowRight } from 'lucide-react';
import { api } from '../../lib/api';
import { formatDate } from '../../lib/utils';

interface Team {
  id: string;
  name: string;
  slug: string;
  description?: string;
  contactEmail: string;
  contactPhone?: string;
  status: string;
  logoUrl?: string;
  memberCount: number;
  userRole: string;
}

interface Booking {
  id: string;
  trackName: string;
  bookingDate: string;
  startTime: string;
  endTime: string;
  status: string;
}

export function TeamDashboard() {
  const { slug } = useParams<{ slug: string }>();
  const [team, setTeam] = useState<Team | null>(null);
  const [recentBookings, setRecentBookings] = useState<Booking[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    async function fetchTeamData() {
      try {
        const [teamRes, bookingsRes] = await Promise.all([
          api.get(`/api/teams/${slug}`),
          api.get(`/api/teams/${slug}/bookings?limit=5`),
        ]);

        if (teamRes.team) {
          setTeam(teamRes.team);
        }
        if (bookingsRes.bookings) {
          setRecentBookings(bookingsRes.bookings);
        }
      } catch (error) {
        console.error('Failed to fetch team data:', error);
      } finally {
        setLoading(false);
      }
    }

    if (slug) {
      fetchTeamData();
    }
  }, [slug]);

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-red-600" />
      </div>
    );
  }

  if (!team) {
    return (
      <div className="text-center py-12">
        <h2 className="text-xl font-semibold text-gray-900">Team not found</h2>
        <Link to="/dashboard" className="text-red-600 hover:text-red-700 mt-2 inline-block">
          Return to Dashboard
        </Link>
      </div>
    );
  }

  const canManage = ['owner', 'admin', 'manager'].includes(team.userRole);

  return (
    <div className="space-y-6">
      {/* Team Header */}
      <div className="card p-6">
        <div className="flex items-start space-x-4">
          {team.logoUrl ? (
            <img
              src={team.logoUrl}
              alt={team.name}
              className="w-16 h-16 rounded-lg object-cover"
            />
          ) : (
            <div className="w-16 h-16 bg-gray-200 rounded-lg flex items-center justify-center">
              <span className="text-2xl font-bold text-gray-500">
                {team.name.charAt(0).toUpperCase()}
              </span>
            </div>
          )}
          <div className="flex-1">
            <div className="flex items-center justify-between">
              <h1 className="text-2xl font-bold text-gray-900">{team.name}</h1>
              {canManage && (
                <Link
                  to={`/team/${slug}/settings`}
                  className="btn btn-outline btn-sm"
                >
                  <Settings size={16} className="mr-1" />
                  Settings
                </Link>
              )}
            </div>
            {team.description && (
              <p className="text-gray-600 mt-1">{team.description}</p>
            )}
            <div className="flex items-center space-x-4 mt-3 text-sm text-gray-500">
              <span className="capitalize">Your role: {team.userRole}</span>
              <span>&bull;</span>
              <span>{team.memberCount} members</span>
            </div>
          </div>
        </div>
      </div>

      {/* Quick Stats */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        <Link
          to={`/team/${slug}/members`}
          className="card p-4 hover:shadow-md transition-shadow"
        >
          <div className="flex items-center justify-between">
            <div className="flex items-center space-x-3">
              <div className="w-10 h-10 bg-blue-100 rounded-lg flex items-center justify-center">
                <Users className="text-blue-600" size={20} />
              </div>
              <div>
                <div className="text-2xl font-bold">{team.memberCount}</div>
                <div className="text-sm text-gray-500">Members</div>
              </div>
            </div>
            <ArrowRight className="text-gray-400" size={18} />
          </div>
        </Link>

        <Link
          to={`/team/${slug}/bookings`}
          className="card p-4 hover:shadow-md transition-shadow"
        >
          <div className="flex items-center justify-between">
            <div className="flex items-center space-x-3">
              <div className="w-10 h-10 bg-green-100 rounded-lg flex items-center justify-center">
                <Calendar className="text-green-600" size={20} />
              </div>
              <div>
                <div className="text-2xl font-bold">{recentBookings.length}</div>
                <div className="text-sm text-gray-500">Bookings</div>
              </div>
            </div>
            <ArrowRight className="text-gray-400" size={18} />
          </div>
        </Link>

        <div className="card p-4">
          <div className="flex items-center space-x-3">
            <div className="w-10 h-10 bg-purple-100 rounded-lg flex items-center justify-center">
              <Clock className="text-purple-600" size={20} />
            </div>
            <div>
              <div className="text-2xl font-bold">-</div>
              <div className="text-sm text-gray-500">Track Hours</div>
            </div>
          </div>
        </div>
      </div>

      <div className="grid lg:grid-cols-2 gap-6">
        {/* Recent Bookings */}
        <div className="card">
          <div className="p-4 border-b border-gray-200 flex items-center justify-between">
            <h2 className="font-semibold text-gray-900">Recent Bookings</h2>
            {canManage && (
              <Link
                to={`/team/${slug}/bookings/new`}
                className="text-sm text-red-600 hover:text-red-700"
              >
                + New Booking
              </Link>
            )}
          </div>

          {recentBookings.length === 0 ? (
            <div className="p-8 text-center">
              <Calendar className="mx-auto text-gray-400 mb-3" size={40} />
              <p className="text-gray-600 mb-4">No bookings yet.</p>
              {canManage && (
                <Link
                  to={`/team/${slug}/bookings/new`}
                  className="btn btn-primary btn-sm"
                >
                  Book Track Time
                </Link>
              )}
            </div>
          ) : (
            <div className="divide-y divide-gray-200">
              {recentBookings.map((booking) => (
                <div key={booking.id} className="p-4">
                  <div className="flex items-start justify-between">
                    <div>
                      <div className="font-medium text-gray-900">
                        {booking.trackName}
                      </div>
                      <div className="text-sm text-gray-500">
                        {formatDate(booking.bookingDate)} &bull; {booking.startTime} - {booking.endTime}
                      </div>
                    </div>
                    <span
                      className={`px-2 py-1 text-xs font-medium rounded-full ${
                        booking.status === 'approved'
                          ? 'bg-green-100 text-green-800'
                          : booking.status === 'requested'
                          ? 'bg-yellow-100 text-yellow-800'
                          : booking.status === 'rejected'
                          ? 'bg-red-100 text-red-800'
                          : 'bg-gray-100 text-gray-800'
                      }`}
                    >
                      {booking.status}
                    </span>
                  </div>
                </div>
              ))}
            </div>
          )}

          {recentBookings.length > 0 && (
            <div className="p-4 border-t border-gray-200">
              <Link
                to={`/team/${slug}/bookings`}
                className="text-sm text-gray-600 hover:text-gray-900 flex items-center justify-center"
              >
                View all bookings
                <ArrowRight size={14} className="ml-1" />
              </Link>
            </div>
          )}
        </div>

        {/* Team Info */}
        <div className="card">
          <div className="p-4 border-b border-gray-200">
            <h2 className="font-semibold text-gray-900">Contact Information</h2>
          </div>
          <div className="p-4 space-y-4">
            <div>
              <div className="text-sm text-gray-500">Email</div>
              <a
                href={`mailto:${team.contactEmail}`}
                className="text-gray-900 hover:text-red-600"
              >
                {team.contactEmail}
              </a>
            </div>
            {team.contactPhone && (
              <div>
                <div className="text-sm text-gray-500">Phone</div>
                <a
                  href={`tel:${team.contactPhone}`}
                  className="text-gray-900 hover:text-red-600"
                >
                  {team.contactPhone}
                </a>
              </div>
            )}
            <div>
              <div className="text-sm text-gray-500">Status</div>
              <span
                className={`inline-flex px-2 py-1 text-xs font-medium rounded-full ${
                  team.status === 'active'
                    ? 'bg-green-100 text-green-800'
                    : 'bg-gray-100 text-gray-800'
                }`}
              >
                {team.status}
              </span>
            </div>
          </div>
        </div>
      </div>

      {/* Quick Actions */}
      {canManage && (
        <div className="card p-6">
          <h2 className="font-semibold text-gray-900 mb-4">Quick Actions</h2>
          <div className="flex flex-wrap gap-3">
            <Link to={`/team/${slug}/bookings/new`} className="btn btn-primary">
              <Calendar size={18} className="mr-2" />
              Book Track Time
            </Link>
            <Link to={`/team/${slug}/members`} className="btn btn-outline">
              <Users size={18} className="mr-2" />
              Manage Members
            </Link>
            <Link to={`/team/${slug}/settings`} className="btn btn-outline">
              <Settings size={18} className="mr-2" />
              Team Settings
            </Link>
          </div>
        </div>
      )}
    </div>
  );
}
